import { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from './BlogPage';
import { CTA, Footer } from './App';
import './BlogPostPage.css';

const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
    </svg>
  ),
};

/* Simple content renderer */
function renderContent(content) {
  return content.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <h3 key={i} style={{ marginTop: '2rem', marginBottom: '0.75rem' }}>{line.replace(/\*\*/g, '')}</h3>;
    }
    if (line.startsWith('* ')) {
      return <li key={i} style={{ marginLeft: '1.5rem', marginBottom: '0.35rem', color: 'var(--color-text-secondary)' }}>{line.slice(2)}</li>;
    }
    if (line.trim() === '') {
      return <br key={i} />;
    }
    // Bold and links within text
    const parseLine = (text) => {
      const result = [];
      const regex = /(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
      let lastIndex = 0;
      let match;
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          result.push(text.slice(lastIndex, match.index));
        }
        if (match[0].startsWith('**')) {
          result.push(<strong key={match.index}>{match[0].replace(/\*\*/g, '')}</strong>);
        } else if (match[2] && match[3]) {
          result.push(<a key={match.index} href={match[3]} target="_blank" rel="noopener noreferrer" style={{ color: '#C5A55A', textDecoration: 'none' }}>{match[2]}</a>);
        }
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
      }
      return result;
    };
    return (
      <p key={i} style={{ marginBottom: '0.75rem' }}>
        {parseLine(line)}
      </p>
    );
  });
}

function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="blogpost-page">
      <Helmet>
        <title>{post.title} | Mission Critical Support Group</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://www.missioncriticalsupportgroup.com/blog/${post.slug}`} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.missioncriticalsupportgroup.com/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | Mission Critical Support Group`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:site_name" content="Mission Critical Support Group" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:section" content={post.category} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "author": {
          "@type": "Organization",
          "name": "Mission Critical Support Group",
          "url": "https://www.missioncriticalsupportgroup.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Mission Critical Support Group",
          "url": "https://www.missioncriticalsupportgroup.com"
        },
        "datePublished": post.date,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.missioncriticalsupportgroup.com/blog/${post.slug}`
        },
        "articleSection": post.category
      }) }} />

      {/* Nav */}
      <nav className="blogpost-nav">
        <div className="blogpost-nav-inner">
          <a href="/blog" className="blogpost-nav-back"><Icons.ArrowLeft /><span>Back to Stories</span></a>
        </div>
      </nav>

      {/* Hero */}
      <section className="blogpost-hero" style={{ background: post.image ? `linear-gradient(rgba(27,42,74,0.7), rgba(27,42,74,0.7)), url(${post.image}) center/cover no-repeat` : 'linear-gradient(135deg, #1B2A4A 0%, #2a3f6a 100%)' }}>
        <div className="blogpost-hero-inner">
          <div className="blogpost-meta">
            <span className="blogpost-category">{post.category}</span>
            <span>{post.date}</span>
            <span>{post.readTime}</span>
          </div>
          <h1 style={{ color: '#FFFFFF' }}>{post.title}</h1>
        </div>
      </section>

      {/* Article */}
      <article className="blogpost-article">
        <div className="blogpost-article-inner">
          {renderContent(post.content)}
        </div>
      </article>

      <CTA />
      <Footer />
    </div>
  );
}

export default BlogPostPage;
